import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import logoZitata from '../assets/images/logo_zitata.png';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (!token) {
      setError('Token de réinitialisation manquant');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      const response = await authService.resetPassword(token, password);
      
      if (response.success) {
        setSuccess(true);
        // Rediriger vers login après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.message || 'Une erreur est survenue');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Token invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  // Si pas de token, afficher un message d'erreur
  if (!token) {
    return (
      <div style={{ 
        background: '#121212', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#fff',
        fontFamily: "system-ui, -apple-system, sans-serif"
      }}>
        <div style={{
          background: '#23262a',
          borderRadius: '12px',
          padding: '48px',
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          border: '1px solid #353a40',
          textAlign: 'center'
        }}>
          <img 
            src={logoZitata} 
            alt="Zitata" 
            style={{
              height: '160px',
              marginBottom: '24px',
              maxWidth: '100%'
            }}
          />
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#f87171',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <p style={{ margin: 0, fontWeight: '500' }}>
              Lien de réinitialisation invalide
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
              Le lien est invalide ou a expiré. Veuillez faire une nouvelle demande.
            </p>
          </div>
          
          <Link 
            to="/forgot-password"
            style={{
              display: 'inline-block',
              background: '#f59e0b',
              color: '#121212',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              marginBottom: '16px'
            }}
          >
            Nouvelle demande
          </Link>
          
          <div>
            <Link 
              to="/login"
              style={{
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '0.9rem'
              }}
            >
              ← Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: '#121212', 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      color: '#fff',
      fontFamily: "system-ui, -apple-system, sans-serif"
    }}>
      <div style={{
        background: '#23262a',
        borderRadius: '12px',
        padding: '48px',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        border: '1px solid #353a40'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img 
            src={logoZitata} 
            alt="Zitata" 
            style={{
              height: '160px',
              marginBottom: '16px',
              maxWidth: '100%'
            }}
          />
          <h1 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            margin: '0 0 8px 0',
            color: '#fff'
          }}>
            Nouveau mot de passe
          </h1>
          <p style={{
            fontSize: '0.9rem',
            color: '#94a3b8',
            margin: 0
          }}>
            Choisissez un nouveau mot de passe sécurisé
          </p>
        </div>

        {success ? (
          // Message de succès
          <div>
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              color: '#4ade80',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <svg 
                width="48" 
                height="48" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ marginBottom: '12px' }}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>
                Mot de passe modifié !
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                Vous allez être redirigé vers la page de connexion...
              </p>
            </div>
          </div>
        ) : (
          // Formulaire
          <form onSubmit={handleSubmit}>
            {/* Error Message */}
            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: '0.9em',
                fontWeight: '500'
              }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#94a3b8',
                fontSize: '0.875rem'
              }}>
                Nouveau mot de passe
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    paddingRight: '50px',
                    background: '#121212',
                    border: '1px solid #353a40',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    boxSizing: 'border-box'
                  }}
                  placeholder="Minimum 6 caractères"
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: showPassword ? '#f59e0b' : '#64748b'
                  }}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#94a3b8',
                fontSize: '0.875rem'
              }}>
                Confirmer le mot de passe
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#121212',
                  border: '1px solid #353a40',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontFamily: "system-ui, -apple-system, sans-serif"
                }}
                placeholder="Répétez le mot de passe"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#353a40' : '#f59e0b',
                color: '#121212',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: "system-ui, -apple-system, sans-serif",
                marginBottom: '16px'
              }}
            >
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #121212',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Modification en cours...
                </div>
              ) : 'Modifier le mot de passe'}
            </button>

            <Link 
              to="/login"
              style={{
                display: 'block',
                textAlign: 'center',
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '0.9rem'
              }}
            >
              ← Retour à la connexion
            </Link>
          </form>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        input:focus {
          outline: none;
          border-color: #f59e0b !important;
          box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
        }
        
        button:hover:not(:disabled) {
          background: #fbbf24 !important;
          transform: translateY(-1px);
        }
        
        input::placeholder {
          color: #64748b;
        }
        
        a:hover {
          color: #f59e0b !important;
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;
